import React, { useEffect, useState } from "react";
import { Button, Modal } from "react-bootstrap";
import RecentOrders from "../callLeadDetails/recentOrders/RecentOrders";
import Consultations from "../callLeadDetails/consultations/Consultations";
import Finance from "../callLeadDetails/finance/Finance";
import Claims from "../callLeadDetails/claims/Claims";
import ProfileAvatar from "../callLeadDetails/profileAvatar/ProfileAvatar";
import PackageDetails from "../callLeadDetails/packageDetails/PackageDetails";
import {
  addressText,
  displayBalance,
} from "../callLeadDetails/CallLeadDetails.utils";
import { formatDate, OUTCOMES } from "../callWorkspace.utils";

const DRAWER_TABS = [
  { key: "summary", label: "Call Summary", icon: "phone" },
  { key: "medicine", label: "Medicine Orders", icon: "medkit" },
  { key: "lab", label: "Lab Orders", icon: "flask" },
  { key: "consultations", label: "Consultations", icon: "user-md" },
  { key: "finance", label: "Finance", icon: "credit-card" },
  { key: "claims", label: "Claims", icon: "file-text-o" },
];

const getActivityMetadata = (activity) => {
  if (!activity || !activity.metadata) return {};
  if (typeof activity.metadata === "object") return activity.metadata;
  if (typeof activity.metadata === "string") {
    try {
      return JSON.parse(activity.metadata);
    } catch (error) {
      return {};
    }
  }
  return {};
};

const getActivityEvent = (activity) =>
  String((activity && activity.event_type) || "activity")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (character) => character.toUpperCase());

const getActivityEventClass = (activity) =>
  String((activity && activity.event_type) || "activity")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "") || "activity";

const getActivityStatus = (activity) => {
  const metadata = getActivityMetadata(activity);
  return (metadata.new && metadata.new.outcome) || activity.outcome || "";
};

const getActivityReason = (activity) => {
  const metadata = getActivityMetadata(activity);
  return (
    (metadata.new && metadata.new.reason) ||
    metadata.reason ||
    activity.reason ||
    activity.outcome_reason ||
    "—"
  );
};

const getActivityRemarks = (activity) => {
  const metadata = getActivityMetadata(activity);
  return (
    (metadata.new && metadata.new.remarks) ||
    metadata.remarks ||
    activity.remarks ||
    activity.description ||
    activity.note ||
    activity.channel ||
    "—"
  );
};

const preferredLanguage = (language) =>
  ({
    en: "English",
    si: "Sinhala",
    ta: "Tamil",
    ru: "Russian",
    ar: "Arabic",
    zh: "Chinese",
    hi: "Hindi",
  })[language] || "English";

const LeadDrawer = ({
  lead,
  drawerData,
  onClose,
  onUpdateStatus,
  onSelectTab,
}) => {
  const [addressesOpen, setAddressesOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("summary");
  const details = drawerData && drawerData.details;
  useEffect(() => {
    if (
      details &&
      (activeTab === "medicine" || activeTab === "lab") &&
      onSelectTab
    ) {
      onSelectTab(activeTab);
    }
  }, [details, activeTab, onSelectTab]);
  if (!lead) return null;
  const task = details && details.task;
  const customer = (task && task.lead) || (details && details.lead) || lead;
  const addresses = customer.address || [];
  const packages = customer.lead_catalogue_package || [];
  const isBasicUser = Number(customer.organization_id) === 0;
  const activityRows = (() => {
    const source = drawerData.activities && drawerData.activities.data || [];
    return Array.isArray(source) ? source : source.data || [];
  })();
  const selectTab = (key) => {
    setActiveTab(key);
    if (onSelectTab) onSelectTab(key);
  };
  const retry = (kind) => drawerData.load(kind, true);

  const renderTab = () => {
    // The drawer can render before the lead-details request has populated the
    // The task relation may not be available until the profile request completes.
    if (!task) {
      return (
        <div className="cw-drawer-resource-state">
          {drawerData.loading
            ? "Loading lead details…"
            : "Lead details are unavailable for this record."}
        </div>
      );
    }

    if (activeTab === "summary") {
      const outcome = task.outcome || "not_called";
      return (
        <div className="cw-drawer-summary-content">
          <section className="cw-drawer-section">
            <h3>Call summary</h3>
            <dl className="cw-drawer-call-facts">
              <div>
                <dt>Status</dt>
                <dd>{OUTCOMES[outcome] || outcome.replace(/_/g, " ")}</dd>
              </div>
              {task.outcome_reason || task.remarks ? (
                <div>
                  <dt>Outcome details</dt>
                  <dd>{task.outcome_reason || task.remarks}</dd>
                </div>
              ) : null}
              <div>
                <dt>Call attempts</dt>
                <dd>{task.call_attempts || 0}</dd>
              </div>
              <div>
                <dt>WhatsApp attempts</dt>
                <dd>{task.whatsapp_attempts || 0}</dd>
              </div>
              <div>
                <dt>Next follow-up</dt>
                <dd>
                  {task.next_follow_up_at
                    ? formatDate(task.next_follow_up_at, true)
                    : "—"}
                </dd>
              </div>
              <div>
                <dt>Last contacted</dt>
                <dd>
                  {task.last_contacted_at
                    ? formatDate(task.last_contacted_at, true)
                    : "—"}
                </dd>
              </div>
            </dl>
          </section>
          <section className="cw-drawer-section">
            <h3>Activity</h3>
            {drawerData.activities.loading ? (
              <p className="cw-muted">Loading activity…</p>
            ) : null}
            {drawerData.activities.error ? (
              <p className="cw-tab-error" role="alert">
                {drawerData.activities.error}{" "}
                <Button bsStyle="link" onClick={() => drawerData.loadActivities(drawerData.activities.current_page || 1, customer.id)}>Retry</Button>
              </p>
            ) : null}
            {activityRows.length ? (
              <div
                className="cw-table-wrap cw-drawer-activity-table-wrap"
                tabIndex="0"
                aria-label="Lead activity"
              >
                <table className="cw-table cw-history-table cw-drawer-activity-table">
                  <thead>
                    <tr>
                      <th>Event</th>
                      <th>Task</th>
                      <th>Status</th>
                      <th>Reason</th>
                      <th>Changed by</th>
                      <th>Date</th>
                      <th>Remarks</th>
                    </tr>
                  </thead>
                  <tbody>
                    {activityRows.map((activity, index) => {
                      const activityStatus = getActivityStatus(activity);
                      const statusClass = activityStatus
                        ? String(activityStatus)
                            .toLowerCase()
                            .replace(/\s+/g, "_")
                        : "";
                      return (
                        <tr
                          key={activity.id || index}
                          className={`cw-activity-row cw-activity-row-${getActivityEventClass(activity)}`}
                        >
                          <td className="cw-activity-type">
                            {getActivityEvent(activity)}
                          </td>
                          <td>
                            {(activity.task && activity.task.call_type && activity.task.call_type.name) || "Call task"}
                            {activity.task ? " #" + activity.task.id : ""}
                            {activity.task && activity.task.source && activity.task.source.name ? " · " + activity.task.source.name : ""}
                          </td>
                          <td>
                            {activityStatus ? (
                              <span
                                className={`cw-badge cw-badge-${statusClass}`}
                              >
                                {OUTCOMES[activityStatus] ||
                                  String(activityStatus).replace(/_/g, " ")}
                              </span>
                            ) : (
                              "—"
                            )}
                          </td>
                          <td>{getActivityReason(activity)}</td>
                          <td>
                            {(activity.actor &&
                              (activity.actor.name || activity.actor.email)) ||
                              activity.changed_by ||
                              activity.user_name ||
                              "—"}
                          </td>
                          <td>
                            {formatDate(
                              activity.occurred_at || activity.created_at,
                              true,
                            )}
                          </td>
                          <td>{getActivityRemarks(activity)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : !drawerData.activities.loading && !drawerData.activities.error ? (
              <p className="cw-muted">No activity recorded.</p>
            ) : null}
            {drawerData.activities.total > 0 ? (
              <div className="cw-pagination" aria-label="Lead activity pages">
                <span>{drawerData.activities.total} activities</span>
                <div>
                  <Button type="button" disabled={drawerData.activities.current_page <= 1 || drawerData.activities.loading} onClick={() => drawerData.loadActivities(drawerData.activities.current_page - 1, customer.id)}>Previous</Button>
                  <span>Page {drawerData.activities.current_page} of {drawerData.activities.last_page}</span>
                  <Button type="button" disabled={drawerData.activities.current_page >= drawerData.activities.last_page || drawerData.activities.loading} onClick={() => drawerData.loadActivities(drawerData.activities.current_page + 1, customer.id)}>Next</Button>
                </div>
              </div>
            ) : null}
          </section>
        </div>
      );
    }
    if (activeTab === "medicine" || activeTab === "lab") {
      const resource =
        activeTab === "medicine"
          ? drawerData.medicineOrders
          : drawerData.labOrders;
      return resource.error ? (
        <div className="cw-tab-error" role="alert">
          {resource.error}{" "}
          <Button bsStyle="link" onClick={() => retry("orders")}>
            Retry
          </Button>
        </div>
      ) : (
        <RecentOrders
          resource={resource}
          formatDate={formatDate}
          leadId={customer.id}
          leadName={customer.name}
          type={activeTab}
          compact
        />
      );
    }
    if (activeTab === "consultations")
      return drawerData.consultations.error ? (
        <div className="cw-tab-error" role="alert">
          {drawerData.consultations.error}{" "}
          <Button bsStyle="link" onClick={() => retry("consultations")}>
            Retry
          </Button>
        </div>
      ) : (
        <Consultations
          resource={drawerData.consultations}
          formatDate={formatDate}
          onPage={(page) => drawerData.loadPage("consultations", page, true)}
        />
      );
    if (activeTab === "finance")
      return drawerData.finance.cardsError || drawerData.finance.bankError ? (
        <div className="cw-tab-error" role="alert">
          {drawerData.finance.cardsError || drawerData.finance.bankError}{" "}
          <Button bsStyle="link" onClick={() => retry("finance")}>
            Retry
          </Button>
        </div>
      ) : (
        <Finance finance={drawerData.finance} />
      );
    return (
      <>
        {drawerData.claims.error ? (
          <div className="cw-tab-error" role="alert">
            {drawerData.claims.error}{" "}
            <Button bsStyle="link" onClick={() => retry("claims")}>
              Retry claims
            </Button>
          </div>
        ) : null}
        {drawerData.claimTransactions.error ? (
          <div className="cw-tab-error" role="alert">
            {drawerData.claimTransactions.error}{" "}
            <Button bsStyle="link" onClick={() => retry("transactions")}>
              Retry transactions
            </Button>
          </div>
        ) : null}
        {drawerData.policies.error ? (
          <div className="cw-tab-error" role="alert">
            {drawerData.policies.error}{" "}
            <Button
              bsStyle="link"
              onClick={() => drawerData.loadPolicies(true)}
            >
              Retry policy details
            </Button>
          </div>
        ) : null}
        <Claims
          claims={drawerData.claims}
          transactions={drawerData.claimTransactions}
          policies={drawerData.policies}
          packages={packages}
          formatDate={formatDate}
          onClaimsPage={(page) => drawerData.loadPage("claims", page, true)}
          onTransactionsPage={(page) =>
            drawerData.loadPage("transactions", page, true)
          }
        />
      </>
    );
  };

  return (
    <aside
      className="cw-lead-drawer"
      role="dialog"
      aria-modal="true"
      aria-label="Lead details"
    >
      <div className="cw-drawer-header">
        <strong className="cw-drawer-batch-name">{lead.batchName}</strong>
        <button
          type="button"
          className="cw-drawer-close"
          aria-label="Close lead details"
          onClick={onClose}
        >
          ×
        </button>
      </div>
      <div className="cw-drawer-body">
        {drawerData && drawerData.loading && !details ? (
          <div className="cw-drawer-resource-state">Loading lead details…</div>
        ) : null}
        {details ? (
          <section
            className="cw-panel cw-profile-card cw-drawer-profile-card"
            aria-labelledby="cw-drawer-customer-name"
          >
            <div className="cw-profile-top">
              <div className="cw-profile-summary">
                <ProfileAvatar
                  name={customer.name || lead.name}
                  imageUrl={customer.profile_picture_url}
                />
                <div className="cw-profile-content">
                  <div className="cw-profile-heading">
                    <small className="cw-profile-lead-id">
                      #{customer.id || task.lead_id}
                    </small>
                    <div className="cw-drawer-name-row">
                      <h1 id="cw-drawer-customer-name">
                        {customer.name || lead.name}
                      </h1>
                      <button
                        type="button"
                        className="cw-drawer-address-icon"
                        aria-label={`Show addresses (${addresses.length})`}
                        title={`Show addresses (${addresses.length})`}
                        onClick={() => setAddressesOpen(true)}
                      >
                        <i className="fa fa-map-marker" aria-hidden="true" />
                      </button>
                    </div>
                    <strong className="cw-drawer-profile-phone">
                      {customer.mobile_no || lead.phone}
                    </strong>
                    <p>
                      Language: {preferredLanguage(customer.preferred_language)}
                    </p>
                  </div>
                  {/* <Button
                    className="cw-address-button"
                    type="button"
                    onClick={() => setAddressesOpen(true)}
                  >
                    <i className="fa fa-map-marker" aria-hidden="true" /> Show
                    addresses ({addresses.length})
                  </Button> */}
                </div>
              </div>
              <div className="cw-profile-side">
                <div className="cw-balance-grid">
                  {!isBasicUser &&
                  details.ipd_claim_balance &&
                  details.ipd_claim_balance.available &&
                  details.ipd_claim_balance.available.remainingCredit !== 0 ? (
                    <div className="cw-balance-card cw-balance-ipd">
                      <span className="cw-balance-label">
                        <i className="fa fa-hospital-o" aria-hidden="true" />{" "}
                        IPD Claim
                      </span>
                      <strong>
                        {displayBalance(details.ipd_claim_balance)}
                      </strong>
                    </div>
                  ) : null}
                  {!isBasicUser &&
                  details.opd_claim_balance &&
                  details.opd_claim_balance.available &&
                  details.opd_claim_balance.available.remainingCredit !== 0 ? (
                    <div className="cw-balance-card cw-balance-opd">
                      <span className="cw-balance-label">
                        <i className="fa fa-stethoscope" aria-hidden="true" />{" "}
                        OPD Claim
                      </span>
                      <strong>
                        {displayBalance(details.opd_claim_balance)}
                      </strong>
                    </div>
                  ) : null}
                  {details.wallet_balance &&
                  details.wallet_balance.available &&
                  details.wallet_balance.available.remainingCredit !== 0 ? (
                    <div className="cw-balance-card cw-balance-wallet">
                      <span className="cw-balance-label">
                        <i className="fa fa-credit-card" aria-hidden="true" />{" "}
                        Flash Credits
                      </span>
                      <strong>{displayBalance(details.wallet_balance)}</strong>
                    </div>
                  ) : null}
                </div>
              </div>
            </div>
            {packages.length ? (
              <>
                <div className="cw-profile-divider" />
                <div className="cw-profile-packages cw-drawer-packages">
                  <h2>Packages and discounts</h2>
                  <ul className="cw-plain-list">
                    {packages.map((item) => (
                      <PackageDetails
                        key={item.id}
                        item={item}
                        isBasicUser={isBasicUser}
                      />
                    ))}
                  </ul>
                </div>
              </>
            ) : null}
          </section>
        ) : null}
        <section className="cw-panel cw-history-panel cw-drawer-history-panel">
          <div
            className="cw-history-tabs"
            role="tablist"
            aria-label="Lead details"
          >
            {DRAWER_TABS.map((tab) => (
              <button
                key={tab.key}
                type="button"
                role="tab"
                id={`cw-drawer-tab-${tab.key}`}
                aria-controls="cw-drawer-tabpanel"
                aria-selected={activeTab === tab.key}
                className={
                  activeTab === tab.key
                    ? "cw-history-tab is-active"
                    : "cw-history-tab"
                }
                onClick={() => selectTab(tab.key)}
              >
                <i className={`fa fa-${tab.icon}`} aria-hidden="true" />{" "}
                {tab.label}
              </button>
            ))}
          </div>
          <div
            className="cw-history-content cw-drawer-tab-content"
            id="cw-drawer-tabpanel"
            role="tabpanel"
            aria-labelledby={`cw-drawer-tab-${activeTab}`}
          >
            {drawerData.loading ? (
              <div className="cw-drawer-resource-state">
                Loading lead details…
              </div>
            ) : drawerData.error ? (
              <div className="cw-tab-error" role="alert">
                {drawerData.error}
              </div>
            ) : (
              renderTab()
            )}
          </div>
        </section>
      </div>
      <div className="cw-drawer-footer">
        <Button type="button" onClick={() => onUpdateStatus(lead)}>
          Update status
        </Button>
      </div>
      <Modal
        show={addressesOpen}
        onHide={() => setAddressesOpen(false)}
        aria-labelledby="drawer-addresses-modal-title"
      >
        <Modal.Header closeButton>
          <Modal.Title id="drawer-addresses-modal-title">
            Customer addresses
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {addresses.length ? (
            <ol className="cw-address-list">
              {addresses.map((address, index) => (
                <li key={address.id || index}>
                  <i className="fa fa-map-marker" aria-hidden="true" />
                  <span>{addressText(address)}</span>
                </li>
              ))}
            </ol>
          ) : (
            <p className="cw-muted">No addresses available.</p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={() => setAddressesOpen(false)}>Close</Button>
        </Modal.Footer>
      </Modal>
    </aside>
  );
};

export default LeadDrawer;
